import React, { useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { useTest } from '@hooks/useTest'
import { Link } from 'gatsby'
import { productUrl } from '@helpers/route'
import { useBaseCollectionTileContext } from '../BaseCollectionTileContext'

const RoomOptionsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  alignItems: 'center',
  color: theme.palette.secondary.dark,
  fontSize: '14px',
  marginTop: '4px',
  lineHeight: 'normal',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    marginTop: '-4px',
  },
}))

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  zIndex: 1,
  '&:hover': {
    textDecoration: 'underline',
  },
}))

const RoomOptions = () => {
  const { collection, activeColor } = useBaseCollectionTileContext()
  const { roomOptions, roomVariationSlots, variations } = collection || {}
  const { generateTestId } = useTest()

  // This is a memoized object that converts the room options to a more readable format
  // and determines the route for each option
  const summaryRoomOptionsConverter = useMemo(() => {
    const bedStorageRoute = variations?.[activeColor]?.route
    const loftBedRoute = roomVariationSlots?.[activeColor]?.['loft bed']?.route
    const cribRoute = roomVariationSlots?.[activeColor]?.crib?.route
    const bunkBedRoute = roomVariationSlots?.[activeColor]?.['bunk bed']?.route

    return {
      'bed storage': {
        label: 'Storage',
        link: bedStorageRoute ? productUrl(bedStorageRoute) : undefined,
      },
      'loft bed': {
        label: 'Loft Bed',
        link: loftBedRoute ? productUrl(loftBedRoute) : undefined,
      },
      crib: {
        label: 'Crib',
        link: cribRoute ? productUrl(cribRoute) : undefined,
      },
      'bunk bed': {
        label: 'Bunk Bed',
        link: bunkBedRoute ? productUrl(bunkBedRoute) : undefined,
      },
    }
  }, [activeColor, variations, roomVariationSlots])

  if (!roomOptions || Object.keys(roomOptions).length === 0) return null

  const roomOptionsArray = Object.entries(roomOptions)

  return (
    <RoomOptionsWrapper data-testid={generateTestId('collection-tile', 'room-options')}>
      <span>Options:</span>
      {roomOptionsArray?.map(([option, value], index) => {
        const convertedOption = summaryRoomOptionsConverter[option.toLowerCase()]?.label ?? option
        const route = summaryRoomOptionsConverter[option.toLowerCase()]?.link

        // Hide the "More Room Options" option
        if (convertedOption === 'More Room Options') return null

        return (
          <React.Fragment key={convertedOption}>
            {route ? (
              <StyledLink to={route} aria-label={`View ${convertedOption} options`} tabIndex={0} role="link">
                {convertedOption}
              </StyledLink>
            ) : (
              <span>{convertedOption}</span>
            )}
            {index < roomOptionsArray.length - 1 && <span aria-hidden="true">•</span>}
          </React.Fragment>
        )
      })}
    </RoomOptionsWrapper>
  )
}

export default RoomOptions
